import React, { useEffect, useState } from 'react'
import { collection, query, where, getDocs } from 'firebase/firestore'
import { db, auth } from '../../firebase'
import { useSchoolId } from '../../lib/auth/schoolContext'
import { Icons } from '../../lib/Icons'
import { useNavigate } from 'react-router-dom'

const getExpectedSessionsPerWeek = (frequency) => {
  const freq = (frequency || '').toLowerCase()
  if (freq.includes('daily')) return 5
  if (freq.includes('twice') || freq === '2x') return 2
  if (freq.includes('weekly')) return 1
  return 1 // default
}

const getCurrentWeekDays = () => {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
  const today = new Date()
  const currentDay = days[today.getDay()]
  const dayOfWeek = today.getDay()
  
  // Get days from start of week (Monday) to today
  const weekStart = new Date(today)
  weekStart.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1))
  
  return { currentDay, weekStart, days }
}

export default function InterventionGroupReminders() {
  const { schoolId } = useSchoolId()
  const navigate = useNavigate()
  const [reminders, setReminders] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchGroupReminders = async () => {
      if (!schoolId || !auth.currentUser) return

      try {
        const currentUserId = auth.currentUser.uid
        const groupsQuery = query(
          collection(db, 'interventionGroups'),
          where('schoolId', '==', schoolId),
          where('status', '==', 'active'),
          where('staffLead', '==', currentUserId)
        )

        const snapshot = await getDocs(groupsQuery)
        const groups = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))

        const { weekStart } = getCurrentWeekDays()
        const remindersList = []

        groups.forEach(group => {
          const expectedPerWeek = getExpectedSessionsPerWeek(group.schedule?.frequency)
          
          // Count sessions this week
          const sessionsThisWeek = (group.sessions || []).filter(session => {
            if (!session.date) return false
            const sessionDate = session.date.toDate ? session.date.toDate() : new Date(session.date)
            return sessionDate >= weekStart
          })

          const needsDataEntry = sessionsThisWeek.length < expectedPerWeek

          remindersList.push({
            id: group.id,
            name: group.name,
            expectedSessions: expectedPerWeek,
            loggedSessions: sessionsThisWeek.length,
            needsDataEntry,
            schedule: group.schedule
          })
        })

        setReminders(remindersList)
      } catch (error) {
        console.error('Error fetching intervention group reminders:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchGroupReminders()
  }, [schoolId])

  if (loading) {
    return (
      <div className="overflow-hidden rounded-lg bg-white shadow-sm">
        <div className="p-6">
          <h2 className="text-base font-medium text-gray-900">Your Intervention Groups</h2>
          <div className="mt-6 space-y-3">
            <div className="h-16 bg-gray-100 rounded animate-pulse" />
            <div className="h-16 bg-gray-100 rounded animate-pulse" />
          </div>
        </div>
      </div>
    )
  }

  if (reminders.length === 0) {
    return (
      <div className="overflow-hidden rounded-lg bg-white shadow-sm">
        <div className="p-6">
          <h2 className="text-base font-medium text-gray-900">Your Intervention Groups</h2>
          <p className="mt-3 text-sm text-gray-600">
            You are not currently assigned to lead any intervention groups.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-lg bg-white shadow-sm">
      <div className="p-6">
        <h2 className="text-base font-medium text-gray-900">Your Intervention Groups</h2>
        <div className="mt-6 flow-root">
          <ul className="-my-5 divide-y divide-gray-200">
            {reminders.map((reminder) => (
              <li key={reminder.id} className="py-4">
                <div className="flex items-start space-x-3">
                  <div className="shrink-0">
                    {reminder.needsDataEntry ? (
                      <Icons.ExclamationTriangle className="h-6 w-6 text-amber-500" aria-hidden="true" />
                    ) : (
                      <Icons.CheckCircle className="h-6 w-6 text-green-500" aria-hidden="true" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900">{reminder.name}</p>
                    <p className="text-sm text-gray-600">
                      {reminder.loggedSessions} of {reminder.expectedSessions} sessions logged this week
                    </p>
                    {reminder.needsDataEntry && (
                      <p className="text-xs text-amber-600 mt-1">
                        Missing data entry
                      </p>
                    )}
                  </div>
                  <div>
                    <button
                      onClick={() => navigate('/interventions')}
                      className="inline-flex items-center rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50"
                    >
                      Log Session
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-6">
          <button
            onClick={() => navigate('/interventions')}
            className="flex w-full items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset hover:bg-gray-50"
          >
            View all groups
          </button>
        </div>
      </div>
    </div>
  )
}

